import { nextId } from '../utils/ids'

/**
 * Photos an owner has picked, kept in this browser.
 *
 * `menu_core_be` has six upload routes and every one of them is bound to a row
 * it can name: a dish, a menu type, a QR card, the restaurant's own logo. A
 * page in the website builder has no such row, because there is no websites
 * domain yet, so there is nowhere on the server to put a photo. Until there is,
 * a picked photo lives here and `assets/upload.ts` is the one function that
 * changes when the route arrives.
 *
 * IndexedDB and not localStorage: the document itself is persisted there, and
 * one 2 MB photo as base64 is most of the 5 MB a browser gives the whole
 * origin. Blobs in IndexedDB cost their own size and nothing else.
 */

const DB_NAME = 'website-builder-assets'
const DB_VERSION = 1
const STORE = 'photos'

/** What a reference to a browser-held photo looks like inside a document. */
export const LOCAL_PREFIX = 'local:'

export const isLocalAsset = (ref: string) => ref.startsWith(LOCAL_PREFIX)

export interface StoredAsset {
  id: string
  blob: Blob
  mime: string
  /** What the owner's file was called, for a sentence about it later. */
  name: string
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function run<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return open().then(
    db =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode)
        const request = work(transaction.objectStore(STORE))
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
        transaction.oncomplete = () => db.close()
      })
  )
}

/** Stores a picked photo and hands back the reference a document holds. */
export async function putLocalAsset(blob: Blob, name: string): Promise<string> {
  const id = nextId('photo')
  const asset: StoredAsset = {
    id,
    blob,
    mime: blob.type || 'image/webp',
    name
  }
  await run('readwrite', store => store.put(asset))
  return `${LOCAL_PREFIX}${id}`
}

export async function getLocalAsset(ref: string): Promise<StoredAsset | undefined> {
  if (!isLocalAsset(ref)) return undefined
  return run('readonly', store =>
    store.get(ref.slice(LOCAL_PREFIX.length))
  ) as Promise<StoredAsset | undefined>
}

export async function allLocalAssets(): Promise<StoredAsset[]> {
  return run('readonly', store => store.getAll()) as Promise<StoredAsset[]>
}

export async function deleteLocalAsset(ref: string): Promise<void> {
  if (!isLocalAsset(ref)) return
  await run('readwrite', store => store.delete(ref.slice(LOCAL_PREFIX.length)))
}
